import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import Dot from "../../../../icons/Dot";
import ChevronUpSvg from "../../../../icons/ChevronUpSvg";
import ChevronDownSvg from "../../../../icons/ChevronDownSvg";
import MagnifyingGlassIcon from "../../../../icons/MagnifyingGlassIcon";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";

interface OptionProps {
  label?: string;
  value?: string;
}

interface SmartDropdownProps {
  dataSource: () => Promise<OptionProps[]>;
  defaultSelected?: OptionProps;
  onOpenChangeHandler?: () => void;
  onValueChange: (value: any) => void;
  showSelectedIcon?: boolean;
  placeholderText: string;
  styles?: {
    triggerOuterClass?: string;
    triggerTextClass?: string;
    placeholderClass?: string;
    contentClass?: string;
    inputClass?: string;
    optionsContainerClass?: string;
    optionItemClass?: string;
    noResultsClass?: string;
    formfieldClass?: string;
    showAllOptionAtTop?: boolean;
    allOptionsObject?: OptionProps;
    labelClass?: string;
  };
}

export default function CustomSearchDropdown({
  dataSource,
  defaultSelected,
  onOpenChangeHandler,
  onValueChange,
  showSelectedIcon = false,
  placeholderText,
  styles,
}: SmartDropdownProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<OptionProps[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [selectedValue, setSelectedValue] = useState<OptionProps | undefined>(
    defaultSelected
  );
  const [hasOverflow, setHasOverflow] = useState(false);
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [scrollInterval, setScrollInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await dataSource();
      const sortedData = (data || []).sort((a, b) =>
        (a.label || "").localeCompare(b.label || "")
      );

      if (styles?.showAllOptionAtTop) {
        let finalSortedData = sortedData.filter((item) => {
          return item?.value !== "";
        });
        setOptions(finalSortedData);
      } else {
        setOptions(sortedData);
      }
    };

    fetchData();
  }, [dataSource]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (defaultSelected?.value || styles?.showAllOptionAtTop) {
      setSelectedValue(defaultSelected);
    }
  }, [defaultSelected]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (selectedRef.current) {
          selectedRef.current.scrollIntoView({
            behavior: "auto",
            block: "center",
          });
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [isOpen, selectedValue]);

  const filteredOptions = options.filter((item: any) =>
    item.label?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const isOverflow = container.scrollHeight > container.clientHeight;
      setHasOverflow(isOverflow);
    }
  }, [options, filteredOptions, isOpen]);

  return (
    <Popover
      onOpenChange={(open) => {
        setIsOpen(open);
      }}
      open={isOpen}
    >
      <PopoverTrigger asChild>
        <main
          className={`cursor-pointer py-1 text-web-body-sm pl-2 ${styles?.triggerOuterClass}`}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <div
            className={`flex items-center justify-between ${styles?.formfieldClass}`}
          >
            <p
              className={`text-sm font-openSans text-brand-textBlack flex items-center overflow-hidden ${styles?.triggerTextClass}`}
            >
              {showSelectedIcon && (
                <span className="mr-3">
                  <Dot />
                </span>
              )}
              <span
                className={`text-sm capitalize whitespace-nowrap ${styles?.placeholderClass}`}
              >
                {selectedValue?.label || placeholderText}
              </span>
            </p>
            <div className="pr-2 pl-2">
              {isOpen ? (
                <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
              ) : (
                <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
              )}
            </div>
          </div>
        </main>
      </PopoverTrigger>
      <PopoverContent className={`pb-1 ${styles?.contentClass}`}>
        <div className="min-w-[203px]">
          <div className="flex py-2 items-center">
            <MagnifyingGlassIcon />
            <Input
              type="text"
              placeholder="Search"
              className={`h-5 border-none rounded-sm text-xs ring-none outline-none focus:border-0 focus:outline-none px-0 ml-3 w-full ${styles?.inputClass}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div
            className={`max-h-[400px] overflow-y-scroll border-t-[1px] ${styles?.optionsContainerClass}`}
            ref={containerRef}
          >
            {hasOverflow && (
              <div
                className="sticky top-0 left-0 py-1 z-1 bg-white flex items-center justify-center cursor-pointer"
                onMouseEnter={() => {
                  if (!scrollInterval) {
                    const interval = setInterval(() => {
                      if (containerRef.current) {
                        containerRef.current.scrollBy({
                          top: -50,
                          behavior: "smooth",
                        });
                      }
                    }, 100);
                    setScrollInterval(interval);
                  }
                }}
                onMouseLeave={() => {
                  if (scrollInterval) {
                    clearInterval(scrollInterval);
                    setScrollInterval(null);
                  }
                }}
                onClick={() => {
                  if (scrollInterval) {
                    clearInterval(scrollInterval);
                    setScrollInterval(null);
                  }
                  if (containerRef.current) {
                    containerRef.current.scrollBy({
                      top: -300,
                      behavior: "smooth",
                    });
                  }
                }}
              >
                <ChevronUpSvg />
              </div>
            )}
            {filteredOptions.length > 0 ? (
              <>
                {styles?.showAllOptionAtTop && (
                  <div
                    key={"all_options"}
                    ref={selectedValue?.value === "" ? selectedRef : null}
                    className={`sticky flex justify-between items-center  ${
                      hasOverflow ? "top-7" : "top-0"
                    } px-2 py-1 border-b  cursor-pointer ${
                      selectedValue?.value === "" ? "bg-blue-100" : "bg-white"
                    } `}
                    onClick={() => {
                      onValueChange(
                        styles?.allOptionsObject
                          ? styles?.allOptionsObject
                          : { label: "All Cities", value: "" }
                      );
                      setIsOpen(false);
                    }}
                  >
                    <label
                      htmlFor={`option-all_cities`}
                      className="text-sm pl-2 font-openSans capitalize tracking-wider text-black cursor-pointer"
                    >
                      {styles?.allOptionsObject?.label
                        ? styles?.allOptionsObject?.label
                        : "All Cities"}
                    </label>
                    <Checkbox checked={selectedValue?.value === ""} />
                  </div>
                )}

                {filteredOptions.map((option, index) => (
                  <div
                    key={index}
                    ref={
                      selectedValue?.value === option.value ? selectedRef : null
                    }
                    className={`flex items-center px-2 py-1 cursor-pointer ${
                      selectedValue?.value === option.value ? "bg-blue-100" : ""
                    } ${styles?.optionItemClass}`}
                    onClick={() => {
                      onValueChange(option);
                      setIsOpen(false);
                    }}
                  >
                    <label
                      htmlFor={`option-${index}`}
                      className={`text-sm pl-2 font-openSans capitalize tracking-wider  ${
                        styles?.labelClass
                          ? styles?.labelClass
                          : "text-brand-textLightGrey"
                      }  cursor-pointer`}
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </>
            ) : (
              <div
                className={`text-sm text-gray-500 pl-2 py-2 ${styles?.noResultsClass}`}
              >
                No results found
              </div>
            )}

            {hasOverflow && (
              <div
                className="sticky bottom-0 left-0 py-1 z-1 bg-white flex items-center justify-center cursor-pointer"
                onMouseEnter={() => {
                  if (!scrollInterval) {
                    const interval = setInterval(() => {
                      if (containerRef.current) {
                        containerRef.current.scrollBy({
                          top: 50,
                          behavior: "smooth",
                        });
                      }
                    }, 100);
                    setScrollInterval(interval);
                  }
                }}
                onMouseLeave={() => {
                  if (scrollInterval) {
                    clearInterval(scrollInterval);
                    setScrollInterval(null);
                  }
                }}
                onClick={() => {
                  if (scrollInterval) {
                    clearInterval(scrollInterval);
                    setScrollInterval(null);
                  }
                  if (containerRef.current) {
                    containerRef.current.scrollBy({
                      top: 300,
                      behavior: "smooth",
                    });
                  }
                }}
              >
                <ChevronDownSvg />
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
